import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Search } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import DocumentoFiscal from "../DocumentoFiscal";
import PhoneInput from "../PhoneInput";
import SenhaForte from "../SenhaForte";
import usePaisFiscal, {
	mascararCep,
	regrasVigentes,
} from "../../hooks/usePaisFiscal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const CompanySchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome muito curto")
		.max(80, "Nome muito longo")
		.required("Informe o nome"),
	email: Yup.string().email("E-mail inválido"),
});

const somenteDigitos = (valor) => String(valor || "").replace(/\D/g, "");

// Preenche rua, bairro e cidade pelo CEP. Falhou, os campos seguem editáveis.
const buscarCep = async (cep, setFieldValue) => {
	const digitos = somenteDigitos(cep);

	if (digitos.length !== 8) return;

	try {
		const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
		const dados = await resposta.json();

		if (!dados.erro) {
			setFieldValue("address", dados.logradouro || "");
			setFieldValue("province", dados.bairro || "");
			setFieldValue("city", dados.localidade || "");
			setFieldValue("state", dados.uf || "");
			setFieldValue("postalCode", dados.cep || cep);
		}
	} catch (err) {
		// silencioso de propósito
	}
};

// Consulta dados públicos de um CNPJ brasileiro. Não requer chave. Usa o
// receitaws com fallback para o brasilapi se o primeiro não responder. Os
// dois são gratuitos para volume baixo e sem cadastro.
const buscarCnpj = async (cnpj) => {
	const digitos = somenteDigitos(cnpj);
	if (digitos.length !== 14) return null;

	const tentativas = [
		{
			nome: "receitaws",
			url: `https://receitaws.com.br/v1/cnpj/${digitos}`,
			extrair: (d) => ({
				name: d?.nome || "",
				phone: d?.telefone || "",
				email: d?.email || "",
				postalCode: d?.cep || "",
				address: d?.logradouro || "",
				addressNumber: d?.numero || "",
				addressComplement: d?.complemento || "",
				province: d?.bairro || "",
				city: d?.municipio || "",
				state: d?.uf || "",
				stateRegistration: d?.inscricaoEstadual || "",
				fantasia: d?.fantasia || "",
			}),
		},
		{
			nome: "brasilapi",
			url: `https://brasilapi.com.br/api/cnpj/v1/${digitos}`,
			extrair: (d) => ({
				name: d?.razao_social || "",
				phone: (d?.ddd_telefone_1 ? d.ddd_telefone_1 : "") || d?.telefone || "",
				email: d?.email || "",
				postalCode: d?.cep || "",
				address: d?.logradouro || "",
				addressNumber: d?.numero || "",
				addressComplement: d?.complemento || "",
				province: d?.bairro || "",
				city: d?.municipio || "",
				state: d?.uf || "",
				stateRegistration: "",
				fantasia: d?.nome_fantasia || "",
			}),
		},
	];

	for (const t of tentativas) {
		try {
			const ctrl = new AbortController();
			const timer = setTimeout(() => ctrl.abort(), 8000);
			const resp = await fetch(t.url, { signal: ctrl.signal });
			clearTimeout(timer);
			if (!resp.ok) continue;
			const dados = await resp.json();
			if (!dados || dados.status === "ERROR" || dados.message) continue;
			return t.extrair(dados);
		} catch {
			// próx.
		}
	}

	return null;
};

const SITUACOES = [
	{ value: "active", label: "Ativa" },
	{ value: "suspended", label: "Suspensa" },
	{ value: "blocked", label: "Bloqueada" },
];

const CompanyModal = ({ open, onClose, companyId }) => {
	const regras = usePaisFiscal();
	const initialState = {
		name: "",
		email: "",
		phone: "",
		document: "",
		documentCountry: "",
		postalCode: "",
		address: "",
		addressNumber: "",
		addressComplement: "",
		province: "",
		city: "",
		state: "",
		status: "active",
		dueDate: "",
		planId: "",
		adminName: "",
		adminEmail: "",
		adminPassword: "",
		// Nasce desmarcado: sem marcar, o admin da empresa entra normal.
		adminMustChangePassword: false,
	};

	const [company, setCompany] = useState(initialState);
	const [plans, setPlans] = useState([]);
	const [cnpjCarregando, setCnpjCarregando] = useState(false);
	const timerRef = useRef(null);
	const ultimaChave = useRef("");

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/plans");
				setPlans(data);
			} catch (err) {
				toastError(err);
			}
		})();
	}, [open]);

	useEffect(() => {
		(async () => {
			if (!companyId) return;
			try {
				const { data } = await api.get(`/companies/${companyId}`);
				setCompany((anterior) => ({
					...anterior,
					...data,
					email: data.email || "",
					phone: data.phone || "",
					document: data.document || "",
					documentCountry: data.documentCountry || "",
					postalCode: data.postalCode || "",
					address: data.address || "",
					addressNumber: data.addressNumber || "",
					addressComplement: data.addressComplement || "",
					province: data.province || "",
					city: data.city || "",
					state: data.state || "",
					planId: data.planId ? String(data.planId) : "",
					// O input date só aceita AAAA-MM-DD.
					dueDate: data.dueDate ? String(data.dueDate).slice(0, 10) : "",
				}));
			} catch (err) {
				toastError(err);
			}
		})();

		return () => setCompany(initialState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, open]);

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	const handleClose = () => {
		onClose();
		setCompany(initialState);
		if (timerRef.current) clearTimeout(timerRef.current);
	};

	const dispararBuscaCnpj = (valor, pais, setFieldValue) => {
		const vigente = regrasVigentes(regras, pais);
		const digitos = somenteDigitos(valor);

		// Só Brasil e só CNPJ (14 dígitos). CPF (11) e outros países pulam.
		if (vigente.pais !== "BR" || digitos.length !== 14) return;

		const chave = `${pais}|${digitos}`;
		if (chave === ultimaChave.current) return;

		if (timerRef.current) clearTimeout(timerRef.current);

		timerRef.current = setTimeout(async () => {
			ultimaChave.current = chave;
			setCnpjCarregando(true);
			try {
				const extraidos = await buscarCnpj(digitos);
				if (!extraidos) {
					toast.error("Não achei dados deste CNPJ na Receita.");
					return;
				}
				const candidatos = Object.entries(extraidos).filter(([, v]) =>
					Boolean(v && String(v).trim()),
				);
				let aplicados = 0;
				for (const [campo, valorReceita] of candidatos) {
					const atual = (() => {
						try {
							const x = formikRef?.current?.values;
							if (x && typeof x[campo] === "string") return x[campo];
						} catch {}
						return "";
					})();
					if (campo !== "postalCode" && String(atual || "").trim()) continue;
					const v = String(valorReceita).trim();
					switch (campo) {
						case "postalCode": {
							const cepVig = regrasVigentes(regras, pais);
							setFieldValue(campo, mascararCep(v, cepVig));
							aplicados++;
							break;
						}
						default:
							setFieldValue(campo, v);
							aplicados++;
					}
				}
				if (extraidos.postalCode) {
					await buscarCep(extraidos.postalCode, setFieldValue);
				}
				toast.success(`Dados do CNPJ carregados (${aplicados} campos).`);
			} catch (err) {
				toastError(err);
			} finally {
				setCnpjCarregando(false);
			}
		}, 500);
	};

	const buscarCnpjManual = async (values, setFieldValue) => {
		const vigente = regrasVigentes(regras, values.documentCountry);
		const digitos = somenteDigitos(values.document);
		if (vigente.pais !== "BR" || digitos.length !== 14) {
			toast.error("Digite um CNPJ válido (14 dígitos, Brasil).");
			return;
		}
		ultimaChave.current = "";
		dispararBuscaCnpj(values.document, values.documentCountry, setFieldValue);
	};

	const formikRef = useRef(null);

	const handleSave = async (values) => {
		const payloadBase = {
			...values,
			// Só dígitos: a máscara é para ler na tela. Guardada com pontuação,
			// a busca por documento (que compara dígitos) deixa de encontrar a
			// empresa, e a checagem de duplicado passa a aceitar o mesmo
			// documento duas vezes.
			document: somenteDigitos(values.document),
			postalCode: somenteDigitos(values.postalCode),
			planId: values.planId ? Number(values.planId) : null,
			dueDate: values.dueDate || null,
		};
		// Em edição: só envia dados do admin se o operador digitou algo.
		// Senão vazios seriam interpretados como "apagar nome/e-mail" ou
		// "setar senha vazia" (que o backend não aceita de qualquer forma,
		// mas evita tráfego e warnings desnecessários).
		const adminParcial = {};
		if (companyId) {
			if (String(payloadBase.adminName || "").trim()) {
				adminParcial.adminName = payloadBase.adminName.trim();
			}
			if (String(payloadBase.adminEmail || "").trim()) {
				adminParcial.adminEmail = payloadBase.adminEmail.trim().toLowerCase();
			}
			if (String(payloadBase.adminPassword || "").trim()) {
				adminParcial.adminPassword = payloadBase.adminPassword;
			}
			// A flag só é enviada marcada. Desmarcada, mandar "false" apagaria a
			// exigência que o admin já tinha (a tela de empresa não carrega esse
			// dado do usuário) — quem tira isso é a edição do próprio usuário.
			if (payloadBase.adminMustChangePassword) {
				adminParcial.adminMustChangePassword = true;
			}
			delete payloadBase.adminName;
			delete payloadBase.adminEmail;
			delete payloadBase.adminPassword;
			delete payloadBase.adminMustChangePassword;
		}
		const payload = { ...payloadBase, ...adminParcial };

		try {
			if (companyId) {
				await api.put(`/companies/${companyId}`, payload);
			} else {
				await api.post("/companies", payload);
			}
			toast.success("Empresa salva.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
				<DialogHeader>
					<DialogTitle>
						{companyId ? "Editar empresa" : "Nova empresa"}
					</DialogTitle>
				</DialogHeader>
				<Formik
					innerRef={formikRef}
					initialValues={company}
					enableReinitialize
					validationSchema={CompanySchema}
					onSubmit={(values, actions) => {
						handleSave(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							{/* Duas colunas: à esquerda os dados da empresa e a cobrança; à
							    direita o acesso do administrador. Em tela estreita as
							    colunas empilham sozinhas. */}
							<div className="grid gap-4 md:grid-cols-[1.1fr_1fr] md:gap-5">
							{/* ─── Coluna 1: dados da empresa ─── */}
							<div className="space-y-3">
							<div className="space-y-1.5">
								<Label htmlFor="name">Nome da empresa</Label>
								<Field as={Input} id="name" name="name" autoFocus />
								{/* A linha de erro fica sempre reservada (min-h): surgir e
								    sumir conforme a validação não pode empurrar o resto do
								    formulário para baixo. */}
								<p className="min-h-[1rem] text-xs leading-4 text-destructive">
									{touched.name && errors.name ? errors.name : ""}
								</p>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="email">E-mail</Label>
									<Field as={Input} id="email" name="email" type="email" />
									<p className="min-h-[1rem] text-xs leading-4 text-destructive">
										{touched.email && errors.email ? errors.email : ""}
									</p>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="phone">Telefone</Label>
									<PhoneInput
										id="phone"
										name="phone"
										// O telefone de um cliente estrangeiro é do país dele, não do da
										// plataforma.
										paisPadrao={
											regrasVigentes(regras, values.documentCountry).pais
										}
										value={values.phone}
										onChange={(numero) => setFieldValue("phone", numero)}
									/>
								</div>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<div className="flex items-end gap-2">
										<div className="flex-1">
											<DocumentoFiscal
												paisSelecionado={values.documentCountry || ""}
												onPaisChange={(p) => setFieldValue("documentCountry", p)}
												valor={values.document}
												onValorChange={(v) => {
													setFieldValue("document", v);
													dispararBuscaCnpj(v, values.documentCountry, setFieldValue);
												}}
												erro={touched.document && errors.document}
											/>
										</div>
										<Button
											type="button"
											variant="outline"
											size="icon"
											disabled={cnpjCarregando || isSubmitting}
											onClick={() => buscarCnpjManual(values, setFieldValue)}
											title="Buscar dados pelo CNPJ"
											className="h-10 w-10 shrink-0"
										>
											{cnpjCarregando ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<Search className="h-4 w-4" />
											)}
										</Button>
									</div>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="dueDate">Vencimento</Label>
									<Field as={Input} id="dueDate" name="dueDate" type="date" />
								</div>
							</div>

							{/* Endereço de cobrança: sem CEP e número o Asaas recusa a
							    cobrança no cartão desta empresa. */}
							<div className="space-y-3 rounded-lg border p-3">
								<div>
									<p className="text-sm font-medium">Endereço de cobrança</p>
									<p className="mt-0.5 text-xs text-muted-foreground">
										CEP e número são exigidos pelo Asaas para cobrar no cartão.
									</p>
								</div>

								<div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
									<div className="space-y-1.5">
										<Label htmlFor="postalCode">
											{regrasVigentes(regras, values.documentCountry).rotuloCep}
										</Label>
										<Input
											id="postalCode"
											name="postalCode"
											inputMode="numeric"
											placeholder={
												regrasVigentes(regras, values.documentCountry)
													.exemploCep || "00000-000"
											}
											value={values.postalCode}
											onChange={(e) => {
												const vigente = regrasVigentes(
													regras,
													values.documentCountry,
												);
												const cep = mascararCep(e.target.value, vigente);
												setFieldValue("postalCode", cep);
												// O ViaCEP só conhece CEP brasileiro: fora do Brasil
												// seria uma ida à rede a cada tecla, sempre em vão.
												if (vigente.pais === "BR") buscarCep(cep, setFieldValue);
											}}
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="addressNumber">Número</Label>
										<Field as={Input} id="addressNumber" name="addressNumber" />
									</div>
								</div>

								<div className="grid gap-3 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="address">Endereço</Label>
										<Field as={Input} id="address" name="address" />
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="addressComplement">Complemento</Label>
										<Field
											as={Input}
											id="addressComplement"
											name="addressComplement"
										/>
									</div>
								</div>

								<div className="grid gap-3 sm:grid-cols-[1fr_1fr_5rem]">
									<div className="space-y-1.5">
										<Label htmlFor="province">Bairro</Label>
										<Field as={Input} id="province" name="province" />
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="city">Cidade</Label>
										<Field as={Input} id="city" name="city" />
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="state">UF</Label>
										<Field as={Input} id="state" name="state" maxLength={2} />
									</div>
								</div>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label>Plano</Label>
									<Select
										value={values.planId}
										onValueChange={(valor) => setFieldValue("planId", valor)}
									>
										<SelectTrigger>
											<SelectValue placeholder="Sem plano" />
										</SelectTrigger>
										<SelectContent>
											{plans.map((plan) => (
												<SelectItem key={plan.id} value={String(plan.id)}>
													{plan.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label>Situação</Label>
									<Select
										value={values.status}
										onValueChange={(valor) => setFieldValue("status", valor)}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{SITUACOES.map((item) => (
												<SelectItem key={item.value} value={item.value}>
													{item.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							</div>

							{/* ─── Coluna 2: acesso do administrador ─── */}
							<div className="space-y-3">
							{/* Na criação é obrigatório; na edição serve para recuperar
							    acesso do cliente (redefinir nome/e-mail/senha do admin). */}
							<div className="space-y-3 rounded-lg border p-3">
								<div>
									<p className="text-sm font-medium">
										{companyId
											? "Dados do administrador"
											: "Administrador da empresa"}
									</p>
									<p className="mt-0.5 text-xs text-muted-foreground">
										{companyId
											? "Preencha somente os campos que deseja alterar. Deixe a senha em branco para manter a atual."
											: "Quem vai acessar o sistema por esta empresa."}
									</p>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="adminName">Nome</Label>
									<Field
										as={Input}
										id="adminName"
										name="adminName"
										placeholder={
											companyId ? "Deixe em branco para manter" : ""
										}
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="adminEmail">E-mail de acesso</Label>
									<Field
										as={Input}
										id="adminEmail"
										name="adminEmail"
										type="email"
										placeholder={
											companyId ? "Deixe em branco para manter" : ""
										}
									/>
								</div>
								{/* A senha ganha a linha inteira: com medidor, requisitos e
								    gerador ela é bem mais alta que um campo comum. */}
								<Field name="adminPassword">
									{({ field, form }) => (
										<SenhaForte
											id="adminPassword"
											label="Senha"
											value={field.value || ""}
											onChange={(valor) =>
												form.setFieldValue("adminPassword", valor)
											}
											onBlur={() => form.setFieldTouched("adminPassword", true)}
											nome={values.adminName || values.name}
											email={values.adminEmail || values.email}
											placeholder={
												companyId
													? "Deixe em branco para manter a atual"
													: "Combine uma senha inicial"
											}
											onGerar={() =>
												form.setFieldValue(
													"adminMustChangePassword",
													true
												)
											}
										/>
									)}
								</Field>

								{/* Vale para quem administra a empresa. Marcado, essa pessoa
								    entra uma vez e é obrigada a definir a própria senha. */}
								<div className="flex items-start gap-2.5 border-t border-border pt-3">
									<Checkbox
										id="adminMustChangePassword"
										className="mt-0.5"
										checked={Boolean(values.adminMustChangePassword)}
										onCheckedChange={(marcado) =>
											setFieldValue("adminMustChangePassword", Boolean(marcado))
										}
									/>
									<div className="space-y-0.5">
										<Label htmlFor="adminMustChangePassword" className="cursor-pointer">
											Exigir troca de senha no próximo login
										</Label>
										<p className="text-[11px] text-muted-foreground">
											{companyId
												? "Ao marcar, o admin desta empresa é obrigado a trocar a senha no próximo acesso."
												: "A senha combinada acima é temporária: no primeiro acesso o admin é obrigado a trocar."}
										</p>
									</div>
								</div>
							</div>
							</div>
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									Salvar
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default CompanyModal;
